import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import LoadingDots from "@/components/feature/LoadingDots";
import {
  fetchActiveLiveStream,
  fetchPublicCommunityStats,
  type PublicCommunityStats,
  type PublicLiveStreamItem
} from "@/features/public/api";
import { useRealtimeInvalidation } from "@/shared/realtime/useRealtimeInvalidation";

const numberFormatter = new Intl.NumberFormat("fr-FR");
const heroStadium = "/assets/hero-stadium-night.webp";
const heroStadiumSrcSet = [
  "/assets/hero-stadium-night-640.webp 640w",
  "/assets/hero-stadium-night-960.webp 960w",
  "/assets/hero-stadium-night.webp 1264w"
].join(", ");

const emptyCommunityStats: PublicCommunityStats = {
  activeMembers: 0,
  sportsPracticed: 0,
  activeTeams: 0,
  privateGroups: 0,
  activeChallenges: 0
};

export default function HeroSection() {
  const [visible, setVisible] = useState(false);
  const [activeLive, setActiveLive] = useState<PublicLiveStreamItem | null>(null);
  const [communityStats, setCommunityStats] = useState<PublicCommunityStats | null>(null);

  const loadActiveLive = useCallback(async () => {
    try {
      const stream = await fetchActiveLiveStream();
      setActiveLive(stream);
    } catch {
      setActiveLive(null);
    }
  }, []);

  const loadCommunityStats = useCallback(async () => {
    try {
      const stats = await fetchPublicCommunityStats();
      setCommunityStats(stats);
    } catch {
      setCommunityStats((currentStats) => currentStats ?? emptyCommunityStats);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    void loadActiveLive();
    const intervalId = window.setInterval(() => {
      void loadActiveLive();
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadActiveLive]);

  useEffect(() => {
    void loadCommunityStats();

    const intervalId = window.setInterval(() => {
      void loadCommunityStats();
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadCommunityStats]);

  useRealtimeInvalidation({
    topics: ["lives"],
    onInvalidate: () => {
      void loadActiveLive();
    }
  });

  useRealtimeInvalidation({
    topics: ["community-stats"],
    onInvalidate: () => {
      void loadCommunityStats();
    }
  });

  const resolvedCommunityStats = communityStats ?? emptyCommunityStats;
  const communityStatsLoading = communityStats === null;
  const stats = [
    { value: numberFormatter.format(resolvedCommunityStats.activeMembers), label: "Membres actifs", tone: "accent" as const },
    { value: numberFormatter.format(resolvedCommunityStats.sportsPracticed), label: "Pratiques sportives", tone: "light" as const },
    { value: numberFormatter.format(resolvedCommunityStats.activeTeams), label: "Équipes actives", tone: "accent" as const }
  ];

  return (
    <>
      <style>{`
        @keyframes hero-kenburns { 0% { transform: scale(1.02) translate(0%, 0%); } 100% { transform: scale(1.08) translate(-1%, -1%); } }

        .hero-bg {
          filter: brightness(1.08) contrast(1.12) saturate(1.12);
          image-rendering: high-quality;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          transform-origin: center center;
          will-change: transform;
        }
      `}</style>

      <section className="relative min-h-[80vh] sm:min-h-[90vh] md:min-h-[calc(100vh-80px)] flex items-start overflow-hidden">
        <div className="absolute inset-0 bg-[#050505]">
          <img
            src={heroStadium}
            srcSet={heroStadiumSrcSet}
            sizes="100vw"
            alt="ASC Sonatel - Stade"
            width={1264}
            height={841}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="absolute inset-0 w-full h-full object-cover object-center hero-bg"
            style={{
              animation: "hero-kenburns 16s ease-in-out infinite alternate",
            }}
          />

          {/* Overlay global */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(110deg, rgba(0,0,0,0.98) 0%, rgba(0,0,0,0.7) 38%, rgba(0,0,0,0.2) 100%)",
              zIndex: 20,
            }}
          />
        </div>

        {/* Decorative glows */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-[#E8521A]/8 blur-3xl pointer-events-none z-20" />
        <div className="absolute bottom-1/4 right-1/3 w-64 h-64 rounded-full bg-[#1A8C7A]/8 blur-3xl pointer-events-none z-20" />

        {/* Content */}
        <div className="relative z-30 max-w-[1400px] mx-auto px-5 sm:px-6 w-full pt-10 sm:pt-1 md:pt-1 2xl:pt-14 pb-12 sm:pb-14 md:pb-14 2xl:pb-12 mt-0 sm:-mt-8 md:-mt-10">
          <div className="max-w-3xl">

            {/* Tag */}
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1A8C7A]/20 border border-[#1A8C7A]/40 mb-4 md:mb-5 2xl:mb-10 transition-all duration-700 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              <div className="w-2 h-2 rounded-full bg-[#1A8C7A] animate-pulse" />
              <span className="text-[#4ECDC4] text-sm font-medium tracking-wider uppercase">
                Bienvenue à l&apos;ASC Sonatel
              </span>
            </div>

            {activeLive ? (
              <Link
                to="/live"
                className={`mb-4 md:mb-5 2xl:mb-8 inline-flex items-center gap-3 rounded-full border border-red-400/60 bg-red-500/15 px-4 py-2.5 text-white backdrop-blur-sm shadow-[0_12px_40px_rgba(239,68,68,0.2)] transition-all duration-700 hover:bg-red-500/25 ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
              >
                <span className="relative flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500"></span>
                </span>
                <span className="text-sm font-semibold uppercase tracking-[0.18em]">{activeLive.badgeLabel}</span>
                <span className="hidden sm:inline text-sm text-white/80">{activeLive.title}</span>
                <span className="inline-flex items-center justify-center rounded-full bg-red-500 px-3 py-1 text-xs font-semibold text-white animate-pulse">
                  Voir le live
                </span>
              </Link>
            ) : null}

            {/* Title */}
            <h1
              className={`text-3xl sm:text-4xl md:text-6xl font-extrabold leading-tight mb-3 sm:mb-4 md:mb-5 2xl:mb-8 transition-all duration-700 delay-100 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              <span className="text-white">Ensemble,</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E8521A] to-[#FF8C5A]">
                Nous Bougeons
              </span>
              <br />
              <span className="text-white">Plus Fort</span>
            </h1>

            {/* Subtitle */}
            <p
              className={`text-[15px] sm:text-base md:text-lg text-white/80 leading-relaxed mb-5 sm:mb-6 md:mb-6 2xl:mb-10 max-w-xl transition-all duration-700 delay-200 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              L&apos;Association Sportive et Culturelle de la SONATEL — sport, communauté et culture pour tous les salariés et leurs familles au Sénégal.
            </p>

            {/* CTA Buttons */}
            <div
              className={`flex flex-col sm:flex-row gap-3 mb-5 sm:mb-6 md:mb-6 2xl:mb-10 transition-all duration-700 delay-300 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              <Link
                to="/formulaires?tab=login&redirect=%2Fmembre&source=member-access"
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-7 py-3 rounded-full bg-[#E8521A] text-white font-semibold text-sm hover:bg-[#d44a16] transition-all duration-200 whitespace-nowrap"
                style={{ boxShadow: "0 8px 32px rgba(232, 82, 26, 0.4)" }}
              >
                <i className="ri-user-add-line" />
                Rejoindre l&apos;ASC
              </Link>
              <Link
                to="/club"
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-7 py-3 rounded-full border-2 border-white text-white font-semibold text-sm hover:bg-white hover:text-[#1A1A1A] transition-all duration-200 whitespace-nowrap"
              >
                Découvrir
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            {/* Stats */}
            <div
              className={`grid grid-cols-3 gap-2.5 sm:grid-cols-3 sm:gap-3 md:flex md:flex-wrap md:gap-3 2xl:gap-6 transition-all duration-700 delay-400 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              {stats.map((s, i) => {
                return (
                <div
                  key={i}
                  className="w-full md:w-auto md:min-w-[180px] rounded-3xl border border-white/15 bg-white/10 px-3 sm:px-5 py-3 sm:py-4 backdrop-blur-md shadow-[0_12px_40px_rgba(0,0,0,0.16)]"
                >
                  <div className="flex flex-col" aria-busy={communityStatsLoading}>
                    {communityStatsLoading ? (
                      <LoadingDots
                        className="h-9 justify-start sm:h-10"
                        dotClassName="h-2.5 w-2.5 sm:h-3 sm:w-3"
                        dotColors={s.tone === "light"
                          ? ["bg-white", "bg-[#E8521A]", "bg-[#1A8C7A]"]
                          : ["bg-[#E8521A]", "bg-[#1A8C7A]", "bg-white"]}
                        label={`Chargement ${s.label}`}
                      />
                    ) : (
                      <span className={`text-2xl sm:text-3xl md:text-4xl font-extrabold ${s.tone === "light" ? "text-white" : "text-[#E8521A]"}`}>
                        {s.value}
                      </span>
                    )}
                    <span className={`text-[13px] mt-1 ${s.tone === "light" ? "text-[#E8521A]" : "text-white/75"}`}>
                      {s.label}
                    </span>
                  </div>
                </div>
              );
              })}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
